import React, { useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';

interface PWAInstallPromptProps {
  className?: string;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className = '' }) => {
  const {
    shouldShowInstallPrompt,
    isInstalled,
    platform,
    installPWA,
    dismissInstallPrompt,
    getInstallInstructions,
    canInstall,
    isInstallable
  } = usePWAInstall();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  if (isInstalled || !shouldShowInstallPrompt) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const success = await installPWA();
      if (!success) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('Installation failed:', error);
      setIsExpanded(true);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    dismissInstallPrompt();
  };

  const instructions = getInstallInstructions();

  return (
    <div className={`pwa-install-prompt ${className}`}>
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        right: '20px',
        backgroundColor: '#2563eb',
        color: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 8px 25px rgba(37, 99, 235, 0.3)',
        zIndex: 9999,
        animation: 'slideUp 0.3s ease-out',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
          <div style={{ fontSize: '24px', flexShrink: 0 }}>📱</div>
          
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '16px', 
              fontWeight: '600' 
            }}>
              Instalar Manu
            </h3>
            
            <p style={{ 
              margin: '0 0 12px 0', 
              fontSize: '14px', 
              opacity: 0.9,
              lineHeight: '1.4'
            }}>
              Adicione o app à sua tela inicial para acesso rápido e uma experiência melhor!
            </p>

            {isExpanded && (
              <div style={{ 
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}>
                <p style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '13px', 
                  fontWeight: '600' 
                }}>
                  Como instalar manualmente:
                </p>
                <ol style={{ 
                  margin: 0, 
                  paddingLeft: '16px', 
                  fontSize: '13px',
                  lineHeight: '1.4'
                }}>
                  {instructions.map((instruction, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                style={{
                  backgroundColor: 'white',
                  color: '#2563eb',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isInstalling ? 'not-allowed' : 'pointer',
                  opacity: isInstalling ? 0.7 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isInstalling) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                {isInstalling ? '⏳ Instalando...' : '✨ Instalar'}
              </button>

              {!isExpanded && (
                <button
                  onClick={() => setIsExpanded(true)}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  💡 Como instalar
                </button>
              )}

              <button
                onClick={handleDismiss}
                style={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                ✕ Agora não
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;